<template>
  <div class="businessInfo">
    <review-card name="appBusinessInfoEdit" @edit="triggerModalOpening">
      <edit-business-info v-if="showEditModal" @close="onModalClose" />

      <template #title>{{ t('title') }}</template>

      <div class="data">
        <text-object :label="t('entityTypeLabel')">
          {{ entityType }}
        </text-object>
        <text-object :label="legalNameLabel">
          {{ business.legalBusinessName }}
        </text-object>
        <text-object v-if="!!business.taxId || !!business.abnTaxId" :label="taxIdLabel">
          {{ business.taxId || business.abnTaxId }}
        </text-object>
        <text-object v-if="!!business.doingBusinessAs" :label="doingBusinessAsLabel">
          {{ business.doingBusinessAs }}
        </text-object>
        <text-object v-if="!!trusteeType" :label="t('trusteeTypeLabel')">
          {{ trusteeType }}
        </text-object>
        <text-object v-if="!!business._extension.trusteeName" :label="trusteeNameLabel">
          {{ business._extension.trusteeName }}
        </text-object>
        <text-object v-if="!!business._extension.trusteeTaxId" :label="trusteeTaxIdLabel">
          {{ business._extension.trusteeTaxId }}
        </text-object>
        <text-object
          v-if="!!business._extension.trusteeDoingBusinessAs"
          :label="t('trusteeTradingNameLabel')"
        >
          {{ business._extension.trusteeDoingBusinessAs }}
        </text-object>
        <text-object :label="t('businessAddressLabel')">
          <div class="address">{{ address }}</div>
        </text-object>
        <text-object v-if="!!business.phone" :label="t('businessPhoneLabel')">
          {{ formatPhone(business.phone) }}
        </text-object>
      </div>
    </review-card>
  </div>
</template>
<script>
import ReviewCard from '@/components/ReviewCard';
import TextObject from '@/components/TextObject';
import EditBusinessInfo from '@/views/application/ReviewSubmit/EditBusinessInfo';
import buildTranslators from '@/utils/build-translators';
import phone from '@/filters/phone';
import { COMPANY, TRUST } from '@/modules/legal-entity-types';
import { PARTNERSHIP } from '@/modules/trustee-types';

export default {
  name: 'business-info',
  components: {
    ReviewCard,
    TextObject,
    EditBusinessInfo,
  },
  props: {
    application: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showEditModal: false,
    };
  },
  computed: {
    business() {
      return this.application.business;
    },
    legalNameLabel() {
      const nameLabel =
        this.business.legalEntityType === TRUST ? 'trustNameLabel' : 'businessNameLabel';
      return this.t(nameLabel);
    },
    doingBusinessAsLabel() {
      const nameLabel =
        this.business.legalEntityType === TRUST
          ? 'trustTradingNameLabel'
          : 'businessTradingNameLabel';
      return this.t(nameLabel);
    },
    trusteeNameLabel() {
      return this.t(
        this.business._extension.trusteeType === PARTNERSHIP
          ? 'trusteeNameLabel.PARTNERSHIP'
          : 'trusteeNameLabel.COMPANY',
      );
    },
    taxIdLabel() {
      const { legalEntityType } = this.business;

      if (legalEntityType === TRUST) {
        return this.t('trustAbnLabel');
      }
      return this.t(legalEntityType === COMPANY ? 'businessAcnLabel' : 'businessAbnLabel');
    },
    trusteeTaxIdLabel() {
      return this.t(
        this.business._extension.trusteeType === PARTNERSHIP
          ? 'trusteeAbnLabel'
          : 'trusteeAcnLabel',
      );
    },
    entityType() {
      return this.$t(`businessInfo.legalEntityType.options.${this.business.legalEntityType}`);
    },
    trusteeType() {
      return (
        this.business._extension.trusteeType &&
        this.$t(`businessInfo.trusteeType.options.${this.business._extension.trusteeType}`)
      );
    },
    address() {
      const business = this.business || {};
      const line1 = [
        business.unitNumber,
        business.streetNumber,
        business.streetName,
        business.streetType,
      ]
        .filter(Boolean)
        .join(' ');
      const line2 = [
        [business.city, business.state].filter(Boolean).join(', '),
        business.postalCode,
      ]
        .filter(Boolean)
        .join(' ');
      return [line1, line2].filter(Boolean).join('\n');
    },
  },
  methods: {
    formatPhone: phone,
    triggerModalOpening() {
      this.showEditModal = true;
      document.documentElement.style.overflow = 'hidden';
    },
    onModalClose() {
      this.showEditModal = false;
      this.$emit('modalClose');
      document.documentElement.style.overflow = 'auto';
    },
    t(key, options) {
      return buildTranslators('reviewSubmit.card.businessInfo').t(key, options);
    },
  },
};
</script>
<style lang="sass" scoped>
.data
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  row-gap: $glass-spacing-4
  column-gap: $glass-spacing-3

  +mq($stego)
    grid-template-columns: repeat(3, minmax(0, 1fr))

.address
  white-space: pre-line
</style>
